import { CodeNext, ConceptStatusValidityByCid, FusionError, proposeMountRenderer, serializeFusionError } from '@fusion-advantage/backcompat'
import { getDisplayIdentity, proposeFormSaveDraft, proposeFormSubmitToCode, RootRouteContentType } from "@fusion-advantage/formlib"
import { AppFormRelayImpl } from '@fusion-advantage/formrender'
import { AppThunk } from "../../redux/appStore"
import { setAppContent } from "../../redux/appSlice"
import { updateRenderState } from "../relay/updateRenderState"
import _ from "lodash"
import { replaceMaterialClaims } from "../../dal/ldbMaterial"
import { err, ok, Result } from 'neverthrow'
import { pushLogEntry } from '../../redux/logSlice'
import { updateEncounterStatus } from '../../dal2/updateEncounterStatus'
import { updateWorkItemImport } from '../../dal2/updateWorkItemImport'
import { appError } from '../../redux/errorSlice'
import { selectWritesDisabled } from '../../redux/selectWritesDisabled'
import { selectIsPreviewMode } from '../../redux/selectPreview'

type appFormSaveArgs = {
	appFormRelay: AppFormRelayImpl
	ignoreDraftProposalError?: boolean
	closeOnSuccess: boolean
} & (
	{
		submitToCode: CodeNext
		validityByCid: ConceptStatusValidityByCid
	} | {
		submitToCode?: never
		validityByCid?: never
	}
)

// serializes async saves per encounter guid so that a user-initiated
// save/submit-close can never interleave with an in-flight background
// draft-save on the same encounter. without this, both runs would race
// replaceMaterialClaims + updateWorkItemImport and could produce
// duplicate/inconsistent Import blobs. we serialize (rather than drop the
// second save) so the user's explicit close-save is never lost.
const encounterSaveChains = new Map<string, Promise<unknown>>()

const withEncounterSaveLock = <T>(encounterGuid: string, fn: () => Promise<T>): Promise<T> => {
	const prev = (encounterSaveChains.get(encounterGuid) ?? Promise.resolve()).catch(() => {})
	const result = prev.then(fn)
	// keep a non-rejecting tail in the map so a failed save can't wedge the chain
	const tail = result.catch(() => {})
	encounterSaveChains.set(encounterGuid, tail)
	// best-effort cleanup once settled, if no newer save has queued behind us
	void tail.then(() => {
		if(encounterSaveChains.get(encounterGuid) === tail) {
			encounterSaveChains.delete(encounterGuid)
		}
	})
	return result
}

export const appFormSave = ({
	appFormRelay,
	ignoreDraftProposalError,
	closeOnSuccess,
	submitToCode,
	validityByCid,
}: appFormSaveArgs): AppThunk<Promise<Result<void, FusionError>>> => async (dispatch, getState) => {
	// branch preview: nothing to save, and keep the form open rather than
	// closing to a dashboard that preview doesn't have
	if(selectIsPreviewMode(getState())) {
		if(closeOnSuccess) {
			alert('Branch preview: nothing is saved, and the form stays open.')
		}
		return ok()
	}
	const initialExport = getState().render.form?.export
	if(!initialExport) {
		return err(serializeFusionError('appFormSave', 'expected render.form.export but null'))
	}
	const encounterGuid = initialExport.Form.Encounter.Encounter_GUID
	return withEncounterSaveLock(encounterGuid, async () => {
		// re-read state fresh inside the lock, in case a prior queued save
		// mutated render state while we were waiting our turn
		const state = getState()
		if(!state.render.form || !state.render.form.export) {
			return err(serializeFusionError('appFormSave', 'expected render.form.export but null'))
		}
		const exp = state.render.form.export
		const prevCodeNext = exp.Form.Encounter.CodeNext
		const writeMode = state.render.form.writeMode
		dispatch(pushLogEntry(`[appFormSave] begin save local db draft for encounter ${encounterGuid}`))
		dispatch(updateRenderState({importLoading: true}))
		const rSave = await (async (): Promise<Result<boolean, FusionError>> => {
			// use render state's identity for the save,
			// because the dashboard session behind the form may have expired
			const user = getDisplayIdentity(state.render)
			if(!user) {
				return err(serializeFusionError('appFormSave', 'no user identity available'))
			}
			const rProposeSave = proposeFormSaveDraft(writeMode, prevCodeNext)
			if(rProposeSave.isErr()) {
				if(ignoreDraftProposalError) {
					// silent ignore; no data to sync
					return ok(false)
				}
				return err(serializeFusionError('appFormSave', rProposeSave.error))
			}
			const renderer = state.app.renderer
			if(renderer !== null) {
				const rProposeMount = proposeMountRenderer(renderer)
				if(!rProposeMount.isOk()) {
					return(err(serializeFusionError('appFormSave', rProposeMount.error)))
				}
			}
			// initial write
			{
				const rWrite = await dispatch(writeImport(user.upn, appFormRelay, encounterGuid))
				if(rWrite.isErr()) {
					return err(rWrite.error)
				}
			}
			if(submitToCode) {
				const rProposeSubmit = proposeFormSubmitToCode(prevCodeNext, submitToCode, validityByCid)
				if(rProposeSubmit.isErr()) {
					return err({
						name: 'Error',
						area: 'appFormSave',
						message: `A draft of this form has been saved, but the ${rProposeSubmit.error}.  Please ensure all errors noted in the Form Readiness section have been resolved.`,
						userFriendly: true,
					})
				}
				// past validation checks,
				// make any final adjustments before submission
				const finalizeResult = appFormRelay.finalizeForSubmit({submitToCode})
				if(finalizeResult.detached) {
					return err(serializeFusionError('appFormSave', 'form has been detached, or null import'))
				}
				if(finalizeResult.result?.updated) {
					// re-write
					const rWrite = await dispatch(writeImport(user.upn, appFormRelay, encounterGuid))
					if(rWrite.isErr()) {
						return err(rWrite.error)
					}
				}
				const rStatus = await dispatch(updateEncounterStatus(encounterGuid, submitToCode))
				if(rStatus.isErr()) {
					return err(serializeFusionError('appFormSave', rStatus.error))
				}
			}
			return ok(true)
		})()
		dispatch(updateRenderState({importLoading: false}))
		if(rSave.isErr()) {
			dispatch(appError({
				err: rSave.error,
				alertMessage: rSave.error.userFriendly ? rSave.error.message : undefined,
			}))
			return err(rSave.error)
		}
		if(closeOnSuccess) {
			// returning to the dashboard triggers a silent DashboardBackground
			// sync via backgroundSyncMw (on the content transition), so we no
			// longer fire a sync here. the previous DashboardDeferrable dispatch
			// popped a blocking Sync modal on the Online strategy (it's only
			// deferred on Mobile) — exactly the flash/hang we're removing.
			dispatch(setAppContent({type: RootRouteContentType.Dashboard}))
		}
		return ok()
	})
}

const writeImport = (upn: string, appFormRelay: AppFormRelayImpl, encounterGuid: string): AppThunk<Promise<Result<void, FusionError>>> => async (dispatch, getState) => {
	if(selectWritesDisabled(getState())) {
		return ok()
	}
	const importResult = appFormRelay.generateImport()
	if(importResult.detached || !importResult.result?.dbImport) {
		return err(serializeFusionError('appFormSave', 'form has been detached, or null import'))
	}
	const imp = importResult.result.dbImport
	const materialClaims = imp.FormResponse.Encounter.MaterialClaims ?? []
	const rReplaceClaims = await replaceMaterialClaims(encounterGuid, materialClaims)
	if(rReplaceClaims.isErr()) {
		return err(serializeFusionError('appFormSave', rReplaceClaims.error))
	}
	return await dispatch(updateWorkItemImport(upn, encounterGuid, imp))
}
